import { apiClient } from '../../../utilities/apiClient';
import { showNotification } from '../../../components/snackbar/saga';
import { chatConstants } from '../../../utilities/chatUtils';
import { getSocket } from '../../../utilities/socket';
import { getUserDetails } from '../../PostDetailPage/saga/saga';

// Mark every delivered-to-me message in a conversation as seen. Backend
// emits chat.message.seen events which the gateway pushes to both rooms;
// the receiver's other devices stay in sync.
export const updateMessageStatus = async ({ chatId, recieverId }) => {
  void recieverId; // server scopes by the authenticated user
  const res = await apiClient.get('/messages', {
    params: { conversationId: chatId, direction: 'newer', limit: 200 },
  });
  const ids = (res.data.items ?? [])
    .filter((m) => m.status === chatConstants.delivered)
    .map((m) => m.id);
  for (const id of ids) {
    apiClient.patch(`/messages/${id}/seen`).catch(() => null);
  }
};

// Real-time message stream for a single conversation. Subscribes to the
// gateway's `message:new` and `message:seen` events and filters to the
// active chat. Returns an unsubscribe fn (legacy callers ignore it; new
// callers can hold onto it for cleanup).
export const fetchRealTimeChatMessages = ({ chatId, setMessage }) => {
  const s = getSocket();
  const onNew = (m) => {
    if (m.conversationId === chatId) setMessage(m);
  };
  const onSeen = (m) => {
    if (m.conversationId === chatId) setMessage(m);
  };
  s.on('message:new', onNew);
  s.on('message:seen', onSeen);
  return () => {
    s.off('message:new', onNew);
    s.off('message:seen', onSeen);
  };
};

// Indication stream — fires for every incoming-to-me message regardless of
// conversation. Used by AppTabs to surface a chat-tab dot.
export const fetchRealTimeChatMessagesForIndication = ({ userId, setList }) => {
  void userId;
  const s = getSocket();
  const onNew = (m) => {
    if (m.status === chatConstants.delivered) setList(m);
  };
  s.on('message:new', onNew);
  return () => s.off('message:new', onNew);
};

export const fetchChatMessages = async ({ page, docLimit, chatId, cursor = null }) => {
  void page;
  const res = await apiClient.get('/messages', {
    params: {
      conversationId: chatId,
      direction: 'older',
      limit: docLimit,
      ...(cursor ? { cursor } : {}),
    },
  });
  return res.data.items ?? [];
};

// Upload an asset (image or video) to the same /uploads/asset endpoint
// AddPostPage uses, then return the absolute URL. Used by the chat
// paperclip flow: each picked file uploads, the returned URL becomes the
// `value` of a new chat message (type 'image' or 'video'). Promise-based
// shape so a caller can simply `await` for the URL instead of plumbing
// loading/error/success callbacks (mirrors createMessageInChat).
export const uploadAssetForChat = async (file) => {
  const form = new FormData();
  form.append('file', file);
  try {
    const res = await apiClient.post('/uploads/asset', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res?.data?.url ?? null;
  } catch (e) {
    showNotification({
      isError: true,
      message: e?.response?.data?.message || e?.message || 'upload failed',
    });
    throw e;
  }
};

// Confirm a concierge post_draft. The backend creates the post (status
// PENDING) and patches the source message in place to state='confirmed' +
// postId. We don't update local state from the return — the realtime
// subscription's dedupe-by-id will swap the card in place when the
// backend re-emits messageNew.
export const confirmConciergeDraft = (messageId) =>
  apiClient
    .post('/concierge/confirm-draft', { messageId })
    .then((res) => res?.data?.post ?? null)
    .catch((e) => {
      showNotification({
        isError: true,
        message: e?.response?.data?.message || e?.message || 'confirm failed',
      });
      throw e;
    });

export const declineConciergeDraft = (messageId) =>
  apiClient
    .post('/concierge/decline-draft', { messageId })
    .then(() => true)
    .catch((e) => {
      showNotification({
        isError: true,
        message: e?.response?.data?.message || e?.message || 'decline failed',
      });
      throw e;
    });

export const createMessageInChat = ({ chatId, type, value, senderId, recieverId }) => {
  void senderId; // server reads from cookie
  // Returns the created message row so callers can do an optimistic-UI
  // append without waiting for the Socket.IO round-trip. The realtime
  // subscription in fetchRealTimeChatMessages dedupes by id, so an early
  // append followed by the socket echo is safe (the dedupe replaces the
  // local row in-place; no duplicate bubble renders).
  return apiClient
    .post('/messages', {
      conversationId: chatId,
      receiverId: recieverId,
      type,
      value,
    })
    .then((res) => res?.data?.message ?? null)
    .catch((e) => {
      showNotification({
        isError: true,
        message: e?.response?.data?.message || e?.message || 'send failed',
      });
      throw e;
    });
};

// Real-time conversation updates — handles the "new message landed in
// another chat" / "this chat's lastMessage changed" cases that drove the
// legacy Firestore onSnapshot on the messages collection.
export const fetchRealTimeConversation = ({
  userId,
  conversations,
  setConversations,
  selectedConversationId,
}) => {
  void userId;
  const s = getSocket();
  const onConvUpdated = (conv) => {
    const idx = conversations.findIndex((c) => c.id === conv.id);
    if (idx < 0) return;
    const isOpen = conv.id === selectedConversationId;
    const next = [...conversations];
    next[idx] = {
      ...next[idx],
      lastMessage: conv.lastMessage,
      updatedAt: conv.updatedAt,
      unreadCount: isOpen ? 0 : (next[idx].unreadCount ?? 0) + 1,
    };
    setConversations(next);
  };
  s.on('conversation:updated', onConvUpdated);
  return () => s.off('conversation:updated', onConvUpdated);
};

export const fetchSingleConversation = async ({ chatId, userId }) => {
  const res = await apiClient.get(`/conversations/${chatId}`);
  const conv = res.data.conversation;
  const otherId = conv.users.find((u) => u !== userId);
  const chatUser = otherId ? await getUserDetails({ userId: otherId }) : null;
  return { ...conv, chatUser };
};

export const fetchUnreadCountOfConversation = async ({ chatId, otherData = {}, userId }) => {
  void chatId;
  void userId;
  // Approximation in Phase B: the gateway's conversation:updated events
  // drive per-conversation unread tracking client-side. A backend
  // GET /conversations/:id/unread-count could be added later if needed.
  return { size: 0, otherData };
};

// Real-time list-of-conversations updates — used by the chat-list pane.
// The gateway emits conversation:updated when a new conversation starts;
// we merge it into the local list if it isn't already there.
export const fetchRealTimeUserConversationList = ({ userId, setConversation }) => {
  const s = getSocket();
  const onConvUpdated = (conv) => {
    if (!conv.users.includes(userId)) return;
    fetchSingleConversation({ chatId: conv.id, userId })
      .then((snap) => {
        setConversation({ ...snap, unreadCount: 1 });
      })
      .catch(() => null);
  };
  s.on('conversation:updated', onConvUpdated);
  return () => s.off('conversation:updated', onConvUpdated);
};

export const fetchUserConversations = async ({ page, docLimit, userId, cursor = null }) => {
  void page;
  const res = await apiClient.get('/conversations', {
    params: { limit: docLimit, ...(cursor ? { cursor } : {}) },
  });
  const items = res.data.items ?? [];
  const hydrated = await Promise.all(
    items.map(async (conv) => {
      const otherId = conv.users.find((u) => u !== userId);
      const chatUser = otherId ? await getUserDetails({ userId: otherId }) : null;
      return { ...conv, chatUser, unreadCount: 0 };
    }),
  );
  return hydrated;
};
